import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, MenuItem, Stack, Switch, TextField, Typography } from '@mui/material';
import Editor, { Monaco } from '@monaco-editor/react';
import {
    CommentDto,
    CommentDtoCommentType,
    CommentDtoLevel,
    IConcertAdminDto,
    ILocationAdminDto,
    MessageDto,
    MessageDtoType,
    RestrictionOptionsDto,
    SaleOptionsDto,
} from '../../clients/services';
import { DateTime } from 'luxon';
import { ImageDropZone } from '../AdminShared/ImageDropZone';

interface ConcertDetailPanelProps {
    concert?: IConcertAdminDto;
    locations: ILocationAdminDto[];
    onConcertChange: (updated: IConcertAdminDto) => void;
    onSaveConcert: () => Promise<void>;
    onDeleteConcert: () => Promise<void>;
    onDuplicateConcert: () => Promise<void>;
    onConcertImageDropped: (file: File) => Promise<void>;
    onConcertImagePick: () => void;
    onRemoveConcertImage: (url: string) => Promise<void>;
}

const toDateInput = (value?: DateTime | null): string => {
    return value ? value.toFormat("yyyy-LL-dd'T'HH:mm") : '';
};

const toDateOnlyInput = (value?: DateTime | null): string => {
    return value ? value.toFormat('yyyy-LL-dd') : '';
};

const parseDateInput = (value: string): DateTime | null => {
    if (!value) {
        return null;
    }

    const parsed = DateTime.fromISO(value);
    return parsed.isValid ? parsed : null;
};

const commentLevelOptions = [
    CommentDtoLevel.Info,
    CommentDtoLevel.Warning,
    CommentDtoLevel.Error,
    CommentDtoLevel.Success,
];

const commentTypeOptions = [
    CommentDtoCommentType.Concert,
    CommentDtoCommentType.TicketSale,
    CommentDtoCommentType.InPast,
    CommentDtoCommentType.Canceled,
    CommentDtoCommentType.NoDate,
    CommentDtoCommentType.Postponed,
    CommentDtoCommentType.Alert,
];

const saleOptionsModelUri = 'inmemory://concert-admin/sale-options.json';
const restrictionMessageModelUri = 'inmemory://concert-admin/restriction-message.json';

const saleOptionsSchema = {
    type: 'object',
    additionalProperties: true,
    properties: {
        amountReductions: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: true,
                properties: {
                    id: { type: 'integer' },
                    text: { type: 'string' },
                    amount: { type: 'number' },
                },
            },
        },
        options: {
            type: 'array',
            items: {
                type: 'object',
                additionalProperties: true,
                required: ['id', 'price'],
                properties: {
                    id: { type: 'integer' },
                    text: { type: 'string' },
                    price: { type: 'number' },
                    dependencies: {
                        type: 'array',
                        items: {
                            type: 'object',
                            additionalProperties: true,
                        },
                    },
                    isAdditional: { type: 'boolean' },
                    isReducable: { type: 'boolean' },
                    dependsOn: { type: 'integer' },
                    isDefault: { type: 'boolean' },
                    reduction: {
                        type: 'object',
                        additionalProperties: true,
                    },
                },
            },
        },
    },
};

const restrictionMessageSchema = {
    type: 'object',
    additionalProperties: true,
    properties: {
        code: { type: 'integer' },
        type: {
            type: 'string',
            enum: ['ERROR', 'WARNING', 'INFO', 'SUCCESS'],
        },
        text: { type: 'string' },
    },
};

const configureJsonSchemas = (monaco: Monaco) => {
    const jsonDefaults = (monaco.languages as any).json?.jsonDefaults;
    if (!jsonDefaults) {
        return;
    }

    jsonDefaults.setDiagnosticsOptions({
        validate: true,
        allowComments: false,
        enableSchemaRequest: false,
        schemas: [
            {
                uri: 'concert-admin/sale-options-schema.json',
                fileMatch: [saleOptionsModelUri],
                schema: saleOptionsSchema,
            },
            {
                uri: 'concert-admin/restriction-message-schema.json',
                fileMatch: [restrictionMessageModelUri],
                schema: restrictionMessageSchema,
            },
        ],
    });
};

const compactAdminFormSx = {
    '& .MuiTextField-root .MuiInputBase-input': {
        fontSize: '0.86rem',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.82rem',
    },
    '& .MuiFormHelperText-root': {
        fontSize: '0.72rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.8rem',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 30,
    },
    '& .MuiTypography-h5': {
        fontSize: '1.2rem',
    },
    '& .MuiTypography-h6': {
        fontSize: '1rem',
    },
};

export const ConcertDetailPanel: React.FC<ConcertDetailPanelProps> = ({
    concert,
    locations,
    onConcertChange,
    onSaveConcert,
    onDeleteConcert,
    onDuplicateConcert,
    onConcertImageDropped,
    onConcertImagePick,
    onRemoveConcertImage,
}) => {
    const [saleOptionsRaw, setSaleOptionsRaw] = useState('');
    const [restrictionRaw, setRestrictionRaw] = useState('');
    const [jsonError, setJsonError] = useState('');

    const imagePreview = useMemo(() => {
        if (!concert?.images || concert.images.length === 0) {
            return undefined;
        }
        return concert.images[0]?.url;
    }, [concert]);

    if (!concert) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">Bitte ein Konzert auswaehlen oder erstellen.</Alert>
            </Box>
        );
    }

    const hasTicketSale = concert.ticketSale ?? true;

    return (
        <Box
            sx={{
                p: 1.25,
                height: '100%',
                overflow: 'auto',
                borderLeft: '1px solid',
                borderColor: 'divider',
                backgroundColor: '#ffffff',
                color: '#1f1f1f',
                ...compactAdminFormSx,
            }}
        >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h5">Konzertdetails</Typography>
                {concert.hide && <Chip color="warning" label="VERSTECKT" />}
            </Stack>

            <Stack spacing={1.2}>
                <Typography variant="h6">Allgemein</Typography>
                <TextField
                    label="Titel"
                    value={concert.title || ''}
                    fullWidth
                    onChange={(event) => onConcertChange({ ...concert, title: event.target.value })}
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        label="Künstler"
                        fullWidth
                        value={concert.artistTitle ?? ''}
                        onChange={(event) => onConcertChange({ ...concert, artistTitle: event.target.value || null })}
                    />
                    <TextField
                        label="Datum"
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={toDateOnlyInput(concert.date)}
                        onChange={(event) => onConcertChange({ ...concert, date: parseDateInput(event.target.value) })}
                    />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        label="Doors"
                        fullWidth
                        value={concert.doors ?? ''}
                        onChange={(event) => onConcertChange({ ...concert, doors: event.target.value || null })}
                    />
                    <TextField
                        label="Start"
                        fullWidth
                        value={concert.start ?? ''}
                        onChange={(event) => onConcertChange({ ...concert, start: event.target.value || null })}
                    />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    <TextField
                        select
                        label="Konzertlokal"
                        fullWidth
                        value={concert.locationId ?? ''}
                        onChange={(event) =>
                            onConcertChange({
                                ...concert,
                                locationId: event.target.value ? parseInt(event.target.value as string, 10) : null,
                            })
                        }
                    >
                        <MenuItem value="">-</MenuItem>
                        {locations.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.name} {item.city ? `(${item.city})` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>

                <Typography variant="h6" sx={{ mt: 1 }}>
                    Ticketverkauf
                </Typography>
                <TextField
                    label="Preis"
                    type="number"
                    fullWidth
                    value={concert.price ?? ''}
                    onChange={(event) =>
                        onConcertChange({
                            ...concert,
                            price: event.target.value.length ? Number(event.target.value) : null,
                        })
                    }
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                    {hasTicketSale && (
                        <TextField
                            label="Ticketverkauf Start"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={toDateOnlyInput(concert.ticketSaleStart)}
                            onChange={(event) =>
                                onConcertChange({
                                    ...concert,
                                    ticketSaleStart: parseDateInput(event.target.value),
                                })
                            }
                        />
                    )}
                    {hasTicketSale && (
                        <TextField
                            label="Ticketverkauf Ende"
                            type="datetime-local"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={toDateInput(concert.ticketSaleEnd)}
                            onChange={(event) =>
                                onConcertChange({
                                    ...concert,
                                    ticketSaleEnd: parseDateInput(event.target.value),
                                })
                            }
                        />
                    )}
                </Stack>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <LabeledSwitch
                        label="Mit Ticketverkauf"
                        checked={concert.ticketSale ?? true}
                        onChange={(checked) => onConcertChange({ ...concert, ticketSale: checked })}
                    />
                    {hasTicketSale && (
                        <LabeledSwitch
                            label="Ticketverkauf blockiert"
                            checked={concert.ticketSaleBlocked ?? false}
                            onChange={(checked) => onConcertChange({ ...concert, ticketSaleBlocked: checked })}
                        />
                    )}
                </Stack>

                <Typography variant="h6" sx={{ mt: 1 }}>
                    Spezielle Bedingungen
                </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    <LabeledSwitch
                        label="Versteckt"
                        checked={concert.hide ?? false}
                        onChange={(checked) => onConcertChange({ ...concert, hide: checked })}
                    />
                    <LabeledSwitch
                        label="Ausverkauft"
                        checked={concert.soldOut ?? false}
                        onChange={(checked) => onConcertChange({ ...concert, soldOut: checked })}
                    />
                    <LabeledSwitch
                        label="Konzert abgesagt"
                        checked={concert.concertCancelled ?? false}
                        onChange={(checked) => onConcertChange({ ...concert, concertCancelled: checked })}
                    />
                </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6">Konzertbilder</Typography>
            <ImageDropZone
                label="Konzertbild hinzufuegen"
                imageUrl={imagePreview}
                onFileDropped={onConcertImageDropped}
                onOpenMediaPicker={onConcertImagePick}
            />
            <Stack spacing={1} sx={{ mt: 1 }}>
                {(concert.images ?? []).map((image, index) => (
                    <Stack
                        key={`${image.url}-${index}`}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                    >
                        <Typography variant="body2">{image.url}</Typography>
                        <Button color="error" onClick={async () => onRemoveConcertImage(image.url || '')}>
                            Entfernen
                        </Button>
                    </Stack>
                ))}
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6">Kommentare</Typography>
            <Stack spacing={1}>
                {(concert.comments ?? []).map((comment, index) => (
                    <Box
                        key={`${comment.message}-${index}`}
                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}
                    >
                        <Stack spacing={1}>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    label="Titel"
                                    fullWidth
                                    value={comment.title ?? ''}
                                    onChange={(event) => {
                                        const next = [...(concert.comments ?? [])];
                                        next[index] = new CommentDto({ ...comment, title: event.target.value });
                                        onConcertChange({ ...concert, comments: next });
                                    }}
                                />
                                <Button
                                    color="error"
                                    onClick={() => {
                                        const next = [...(concert.comments ?? [])];
                                        next.splice(index, 1);
                                        onConcertChange({ ...concert, comments: next });
                                    }}
                                >
                                    X
                                </Button>
                            </Stack>
                            <TextField
                                label="Nachricht"
                                fullWidth
                                multiline
                                minRows={2}
                                value={comment.message ?? ''}
                                onChange={(event) => {
                                    const next = [...(concert.comments ?? [])];
                                    next[index] = new CommentDto({ ...comment, message: event.target.value });
                                    onConcertChange({ ...concert, comments: next });
                                }}
                            />
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    select
                                    label="Level"
                                    fullWidth
                                    value={comment.level ?? CommentDtoLevel.Info}
                                    onChange={(event) => {
                                        const next = [...(concert.comments ?? [])];
                                        next[index] = new CommentDto({
                                            ...comment,
                                            level: event.target.value as CommentDtoLevel,
                                        });
                                        onConcertChange({ ...concert, comments: next });
                                    }}
                                >
                                    {commentLevelOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    label="Typ"
                                    fullWidth
                                    value={comment.commentType ?? CommentDtoCommentType.Concert}
                                    onChange={(event) => {
                                        const next = [...(concert.comments ?? [])];
                                        next[index] = new CommentDto({
                                            ...comment,
                                            commentType: event.target.value as CommentDtoCommentType,
                                        });
                                        onConcertChange({ ...concert, comments: next });
                                    }}
                                >
                                    {commentTypeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Stack>
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    onClick={() =>
                        onConcertChange({
                            ...concert,
                            comments: [
                                ...(concert.comments ?? []),
                                new CommentDto({
                                    message: '',
                                    level: CommentDtoLevel.Info,
                                    commentType: CommentDtoCommentType.Concert,
                                }),
                            ],
                        })
                    }
                >
                    Kommentar hinzufuegen
                </Button>
            </Stack>

            <Divider sx={{ my: 2 }} />
            <Typography variant="h6">Verkaufsoptionen (JSON)</Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Editor
                    height="220px"
                    defaultLanguage="json"
                    path={saleOptionsModelUri}
                    value={saleOptionsRaw || JSON.stringify(concert.saleOptions ?? {}, null, 2)}
                    onMount={(_, monaco) => configureJsonSchemas(monaco)}
                    onChange={(value) => setSaleOptionsRaw(value || '')}
                    options={{
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        fontSize: 13,
                        formatOnPaste: true,
                        formatOnType: true,
                        tabSize: 2,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                    }}
                />
            </Box>

            <Typography variant="h6" sx={{ mt: 2 }}>
                Restriktionen
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <LabeledSwitch
                    label="Contact Tracing"
                    checked={concert.restrictionOptions?.requireContactTracing ?? false}
                    onChange={(checked) =>
                        onConcertChange({
                            ...concert,
                            restrictionOptions: new RestrictionOptionsDto({
                                ...(concert.restrictionOptions ?? {}),
                                requireContactTracing: checked,
                            }),
                        })
                    }
                />
                <LabeledSwitch
                    label="Nur Namen"
                    checked={concert.restrictionOptions?.registerNamesOnly ?? false}
                    onChange={(checked) =>
                        onConcertChange({
                            ...concert,
                            restrictionOptions: new RestrictionOptionsDto({
                                ...(concert.restrictionOptions ?? {}),
                                registerNamesOnly: checked,
                            }),
                        })
                    }
                />
            </Stack>
            <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
                Restriction Message (JSON)
            </Typography>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Editor
                    height="180px"
                    defaultLanguage="json"
                    path={restrictionMessageModelUri}
                    value={restrictionRaw || JSON.stringify(concert.restrictionOptions?.message ?? {}, null, 2)}
                    onMount={(_, monaco) => configureJsonSchemas(monaco)}
                    onChange={(value) => setRestrictionRaw(value || '')}
                    options={{
                        minimap: { enabled: false },
                        wordWrap: 'on',
                        fontSize: 13,
                        formatOnPaste: true,
                        formatOnType: true,
                        tabSize: 2,
                        automaticLayout: true,
                        scrollBeyondLastLine: false,
                    }}
                />
            </Box>

            {jsonError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {jsonError}
                </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button
                    variant="contained"
                    onClick={async () => {
                        try {
                            let updated = { ...concert } as IConcertAdminDto;
                            if (saleOptionsRaw.trim().length > 0) {
                                updated = { ...updated, saleOptions: new SaleOptionsDto(JSON.parse(saleOptionsRaw)) };
                            }
                            if (restrictionRaw.trim().length > 0) {
                                const msg = JSON.parse(restrictionRaw);
                                updated = {
                                    ...updated,
                                    restrictionOptions: new RestrictionOptionsDto({
                                        ...(updated.restrictionOptions ?? {}),
                                        message: new MessageDto({
                                            code: msg.code ?? 0,
                                            type: msg.type ?? MessageDtoType.INFO,
                                            text: msg.text ?? '',
                                        }),
                                    }),
                                };
                            }

                            setJsonError('');
                            onConcertChange(updated);
                            await onSaveConcert();
                        } catch (e) {
                            setJsonError('JSON in Verkaufsoptionen oder Restriktionen ist ungueltig.');
                        }
                    }}
                >
                    Konzert speichern
                </Button>
                <Button variant="outlined" onClick={onDuplicateConcert}>
                    Duplizieren
                </Button>
                <Button variant="outlined" color="error" onClick={onDeleteConcert}>
                    Loeschen
                </Button>
            </Stack>
        </Box>
    );
};

const LabeledSwitch = ({
    label,
    checked,
    onChange,
}: {
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <Switch checked={checked} onChange={(event) => onChange(event.target.checked)} />
        <Typography>{label}</Typography>
    </Stack>
);
